/**
 * Tonnie Languages Academy — TanStack Query hooks over the citizen's Academy
 * endpoints, each for a journey (the active one; omitted, the server reads the
 * primary): the Academy opens after that journey's QultureUp assessment. A
 * consent change answers the new state, which becomes the cache; an opening
 * changes the citizen's place (the first fixes the language), so the state is
 * read again — after a refusal too: one in another language
 * (`LANGUAGE_ACADEMY_LANGUAGE_FIXED`) means the page held a stale state.
 *
 * The learner's progress at the school (D6, D8) is part of that state: the
 * World's card reads it through `useLanguageAcademyProgress` — the same query,
 * so the card and the page share one read.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ExternalWorldProgress, LanguageAcademyState } from '@tonnie/types';
import type { LanguageAcademyConsentInput, LanguageAcademyLaunchInput } from '@tonnie/schemas';

import { useSdkContext } from '../providers/SdkProvider';
import { createLanguageAcademyService } from '../services/language-academy.service';

export const languageAcademyKeys = {
  all: ['language-academy'] as const,
  state: (journeyId?: string) =>
    [...languageAcademyKeys.all, 'state', journeyId ?? 'default'] as const,
};

function useLanguageAcademyService() {
  const { client } = useSdkContext();
  return createLanguageAcademyService(client);
}

/** The Academy as the World shows it for the journey — idle while `enabled` is false. */
export function useLanguageAcademy(journeyId?: string, options?: { enabled?: boolean }) {
  const service = useLanguageAcademyService();
  return useQuery({
    queryKey: languageAcademyKeys.state(journeyId),
    queryFn: () => service.getState(journeyId),
    enabled: options?.enabled ?? true,
  });
}

/** The learner's progress the state carries — null before the first opening. */
export function academyProgressOf(state: LanguageAcademyState): ExternalWorldProgress | null {
  return state.progress;
}

/**
 * The learner's progress at the school for the journey — the World's card.
 * The Academy state's own query (one read for the card and the page); idle
 * while `enabled` is false.
 */
export function useLanguageAcademyProgress(journeyId?: string, options?: { enabled?: boolean }) {
  const service = useLanguageAcademyService();
  return useQuery({
    queryKey: languageAcademyKeys.state(journeyId),
    queryFn: () => service.getState(journeyId),
    select: academyProgressOf,
    enabled: options?.enabled ?? true,
  });
}

function useStateWrite<TVariables>(
  journeyId: string | undefined,
  write: (variables: TVariables) => Promise<LanguageAcademyState>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: (state) => queryClient.setQueryData(languageAcademyKeys.state(journeyId), state),
  });
}

/** Accept the current consent text. */
export function useGiveLanguageAcademyConsent(journeyId?: string) {
  const service = useLanguageAcademyService();
  return useStateWrite(journeyId, (input: LanguageAcademyConsentInput) =>
    service.giveConsent(input, journeyId)
  );
}

/** Withdraw the consent. */
export function useRevokeLanguageAcademyConsent(journeyId?: string) {
  const service = useLanguageAcademyService();
  return useStateWrite<void>(journeyId, () => service.revokeConsent(journeyId));
}

/** Open the school from the journey — answers the address to open at once. */
export function useLaunchLanguageAcademy(journeyId?: string) {
  const service = useLanguageAcademyService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LanguageAcademyLaunchInput) => service.launch(input, journeyId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: languageAcademyKeys.all }),
  });
}
